import { useEffect, useState } from "react"
import { usePageContext } from "vike-react/usePageContext"
import { ListCategories, LoadContentTypes } from "../api"
import useSiteLanguage from "./useSiteLanguage"

// Categories / content types are fetched once per language and shared.
// What a product is and which shelves it sits on is part of the page, not
// something to fill in after it: the server renders with these in hand and
// sends them along (pages/+onBeforeRender.js), so the first paint already has
// them and nothing pops in afterwards.
const cache = { categories: {}, contentTypes: {} }
const pending = { categories: {}, contentTypes: {} }
const listeners = new Set()

const notify = () => listeners.forEach((fn) => fn((n) => n + 1))

function load(kind, lang) {
  if (cache[kind][lang] || pending[kind][lang]) return
  const loader = kind === "categories" ? ListCategories : LoadContentTypes
  pending[kind][lang] = loader(lang)
    .then((result) => {
      cache[kind][lang] = result?.list || []
    })
    .catch(() => {
      // a failed fetch is not "no categories": nothing is cached, so the next
      // page that asks tries again (the readers get [] meanwhile)
    })
    .finally(() => {
      delete pending[kind][lang]
      notify()
    })
}

/**
 * Take what the page was rendered with, before anything asks for it — always
 * the newer answer: on the server every page is rendered with lists fetched
 * for it (pages/+onBeforeRender.js), and keeping the first ones instead left
 * a server showing its first categories until a restart.
 */
export function adoptSSRCatalog(ssr) {
  if (!ssr?.language) return
  for (const kind of ["categories", "contentTypes"]) {
    if (!ssr[kind]) continue
    cache[kind][ssr.language] = ssr[kind]
  }
}

/** The two lists for a language, for the server to send with the page. */
export async function getCatalogSSR(language) {
  const [categories, contentTypes] = await Promise.all([
    ListCategories(language).then((r) => r?.list || []).catch(() => []),
    LoadContentTypes(language).then((r) => r?.list || []).catch(() => []),
  ])
  return { language, categories, contentTypes }
}

function useCatalogList(kind) {
  const lang = useSiteLanguage()
  const pageContext = usePageContext()
  const [, setTick] = useState(0)

  // before the first read, so the first render is already the settled one
  adoptSSRCatalog(pageContext?.storefrontCatalog)

  useEffect(() => {
    listeners.add(setTick)
    load(kind, lang)
    return () => listeners.delete(setTick)
  }, [kind, lang])

  const list = cache[kind][lang]
  return { list: list || [], loaded: !!list }
}

export const useCategories = () => useCatalogList("categories")
export const useContentTypes = () => useCatalogList("contentTypes")

export function useContentTypeName(contentTypeId) {
  const { list } = useContentTypes()
  if (!contentTypeId) return null
  return list.find((ct) => ct.contentId === contentTypeId)?.name || null
}

// a product's profile ({ id, name }) by name: the content types list's, in the
// site language, else the one the product came with
export function useProfileName(profile) {
  const name = useContentTypeName(profile?.id)
  if (!profile) return null
  return name || profile.name || null
}
